import { useState, useEffect, useCallback, useContext } from "react";
import { useAxiosPost } from "../../../infrastructure/http";
import { ReceiveComment } from "../../../common/Receive";
import { UserContext } from "../../../providers/user";

interface ReceiveCommentRequest {
	text: string;
	receiveId: string;
	createdByUserId: string;
	createdByUserName: string;
}

interface InvoiceCommentRequest {
	text: string;
	invoiceId: string;
	createdByUserId: string;
	createdByUserName: string;
}

interface TransferCommentRequest {
	text: string;
	createdByUserId: string;
	createdByUserName: string;
}

// Shape returned by Jbg.Transfer.Model.Comment from the BFF
interface TransferCommentResponse {
	id?: string | null;
	content: string;
	userId?: string | null;
	userName?: string | null;
	sourceSystem?: string | null;
	dateCreated?: string | null;
}

interface CommentModalState {
	isOpen: boolean;
}

interface UseCommentsResult {
	comments: ReceiveComment[];
	setComments: (comments: ReceiveComment[]) => void;
	commentModal: CommentModalState;
	onCommentModalOpen: () => void;
	onCommentModalClose: () => void;
	onAddComment: (receiveId: string) => (commentText: string) => Promise<any>;
	onAddInvoiceComment: (invoiceId: string) => (commentText: string) => Promise<any>;
	onAddTransferComment: (transferId: string) => (commentText: string) => Promise<any>;
}

export const useComments = (initialComments: ReceiveComment[] = []): UseCommentsResult => {
	const [comments, setComments] = useState<ReceiveComment[]>(initialComments);
	useEffect(() => {
		// Only update if initialComments length is different from comments otherwise loop is triggered
		if (initialComments.length !== comments.length && comments.length === 0) {
			setComments(initialComments);
		}
	}, [comments.length, initialComments]);

	const [commentModal, setCommentModal] = useState<CommentModalState>({ isOpen: false });
	const { graphProfile } = useContext(UserContext);

	const [, postComment] = useAxiosPost<ReceiveCommentRequest, ReceiveComment>("");
	const [, postInvoiceComment] = useAxiosPost<InvoiceCommentRequest, ReceiveComment>("");
	const [, postTransferComment] = useAxiosPost<TransferCommentRequest, TransferCommentResponse>("");

	const onCommentModalOpen = useCallback(() => {
		setCommentModal({ isOpen: true });
	}, []);

	const onCommentModalClose = useCallback(() => {
		setCommentModal({ isOpen: false });
	}, []);

	const onAddComment = useCallback((receiveId: string) => 
		async (commentText: string) => {
			if (!receiveId) {
				throw new Error("No receive ID available");
			}

			try {
				const response = await postComment(
					{
						text: commentText,
						receiveId,
						createdByUserId: graphProfile.staffId,
						createdByUserName: graphProfile.displayName,
					},
					{
						url: `/receive/${receiveId}/comments`,
						method: "POST",
					}
				);

				const newComment = response.data;
				const updatedComments = [newComment, ...comments];
				setComments(updatedComments);

				return response;
			} catch (error) {
				console.error("Failed to add comment:", error);
				throw error;
			}
		},
		[postComment, comments, graphProfile]
	);

	const onAddInvoiceComment = useCallback((invoiceId: string) => 
		async (commentText: string) => {
			if (!invoiceId) {
				throw new Error("No invoice ID available");
			}

			try {
				const response = await postInvoiceComment(
					{
						text: commentText,
						invoiceId,
						createdByUserId: graphProfile.staffId,
						createdByUserName: graphProfile.displayName,
					},
					{
						url: `/Invoice/${invoiceId}/comments`,
						method: "POST",
					}
				);

				const newComment = response.data;
				newComment.sourceSystem = "Invoice"
				const updatedComments = [newComment, ...comments];
				setComments(updatedComments);

				return response;
			} catch (error) {
				console.error("Failed to add comment:", error);
				throw error;
			}
		},
		[postInvoiceComment, comments, graphProfile]
	);

	const onAddTransferComment = useCallback(
		(transferId: string) =>
			async (commentText: string) => {
				if (!transferId) {
					throw new Error("No transfer ID available");
				}

				try {
					const response = await postTransferComment(
						{
							text: commentText,
							createdByUserId: graphProfile.staffId,
							createdByUserName: graphProfile.displayName,
						},
						{
							url: `/transfer/${transferId}/comments`,
							method: "POST",
						}
					);

					const serverComment = response.data;
					const newComment: ReceiveComment = {
						id: serverComment?.id ?? crypto.randomUUID(),
						receiveId: "",
						createdTimestamp: serverComment?.dateCreated ?? new Date().toISOString(),
						createdByUserId: graphProfile.staffId,
						createdByUserName: graphProfile.displayName,
						text: commentText,
						sourceSystem: serverComment?.sourceSystem ?? "ReceivingApp",
					};
					setComments(prev => [newComment, ...prev]);

					return response;
				} catch (error) {
					console.error("Failed to add transfer comment:", error);
					throw error;
				}
			},
		[postTransferComment, comments, graphProfile]
	);

	return {
		comments,
		setComments,
		commentModal,
		onCommentModalOpen,
		onCommentModalClose,
		onAddComment,
		onAddInvoiceComment,
		onAddTransferComment,
	};
};
